import { DataServiceInterface } from '@/services/interface/DataService.js'
import store from '@/store'
import { backendClient, contractClient } from '@/plugins/apollo'
import { adjustEditability, adjustActions } from '@/utils/state.js'
import lodash from 'lodash'
import { GET_MESSAGES } from '@/graphql/Messages.js'
import * as dashboard from '@/graphql/Dashboard.js'
import * as claims from '@/graphql/Claims'
import * as procedures from '@/graphql/Procedures'
import * as contracts from '@/graphql/Contracts'
import * as orders from '@/graphql/Orders'
import * as company from '@/graphql/Company'
import * as product from '@/graphql/Product'
import * as unit from '@/graphql/Unit'
import * as currency from '@/graphql/Currency'
import * as email from '@/graphql/Email'
import * as file from '@/graphql/File'

export class BackendDataService extends DataServiceInterface {
  static async initialLoad (params) {
    store.dispatch('companyInfo/loadData')
    store.dispatch('dict/load')
    store.dispatch('contract/loadStatuses')
    store.dispatch('order/loadStatuses')
    // return store.dispatch('dashboard/get_data', { from: '2020-01-01', to: '2020-05-31', periodType: 'month' })
  }

  static async getMessages () {
    return backendClient.query({ query: GET_MESSAGES })
  }

  static async getEconomicEfficiency (params) {
    return backendClient.query({ query: dashboard.GET_ECONOMIC_EFFICIENCY, variables: params })
  }

  static async getTendersCount (params) {
    return backendClient.query({ query: dashboard.GET_TENDERS_COUNT, variables: params })
  }

  static async getTendersSum (params) {
    return backendClient.query({ query: dashboard.GET_TENDERS_SUM, variables: params })
  }

  static async getOffersCount (params) {
    return backendClient.query({ query: dashboard.GET_OFFERS_COUNT, variables: params })
  }

  static async getTendersAvgTimeSpending (params) {
    return backendClient.query({ query: dashboard.GET_TENDERS_AVG_TIME_SPENDING, variables: params })
  }

  static async getTendersAvgTimeDuration (params) {
    return backendClient.query({ query: dashboard.GET_TENDERS_AVG_TIME_DURATION, variables: params })
  }

  static async getCountCompaniesInvitedByOrg (params) {
    return backendClient.query({ query: dashboard.GET_COUNT_INVITES_BY_ORG, variables: params })
  }

  static async getCountCompaniesInvitedByTpro (params) {
    return backendClient.query({ query: dashboard.GET_COUNT_INVITES_BY_TPRO, variables: params })
  }

  static async getProvidersTop10 (params) {
    return backendClient.query({ query: dashboard.GET_PROVIDERS_TOP_10, variables: params })
  }

  static async getCriticalIndicators (params) {
    return backendClient.query({ query: dashboard.GET_CRITICAL_INDICATORS, variables: params })
  }

  static async getClaims (params) {
    return backendClient.query({ query: claims.LIST, variables: params })
  }

  static async getClaimDetails (params) {
    return backendClient.query({ query: claims.DETAILS, variables: params })
  }

  static async getClaimsStats (params) {
    return backendClient.query({ query: claims.STATS, variables: params })
  }

  static async getProcedures (params) {
    return backendClient.query({ query: procedures.LIST, variables: params })
  }

  static async getProceduresStats (params) {
    return backendClient.query({ query: procedures.STATS, variables: params })
  }

  static async getProcedureDetails (params) {
    return backendClient.query({ query: procedures.DETAILS, variables: params })
  }

  static async getProcedureLite (params) {
    return backendClient.query({ query: procedures.DETAILS_LITE, variables: params })
      .then(({ data: { procedureLite } }) => {
        return procedureLite
      })
  }

  static async getProcedureWinners (params) {
    return backendClient.query({ query: procedures.WINNERS, variables: params })
  }

  static async getCompanies (params) {
    return backendClient.query({ query: company.LIST, variables: params })
  }

  static async getManagers (params) {
    return backendClient.query({ query: company.MANAGERS, variables: params })
  }

  static async getDepartments (params) {
    return backendClient.query({ query: company.DEPARTMENTS, variables: params })
  }

  static async getProducts (params) {
    return backendClient.query({ query: product.LIST, variables: params })
  }

  static async getUnits (params) {
    return backendClient.query({ query: unit.LIST, variables: params })
  }

  static async getCurrencies (params) {
    return backendClient.query({ query: currency.LIST, variables: params })
  }

  static async createContract (params) {
    return contractClient.mutate({ mutation: contracts.CREATE, variables: params })
  }

  static async getContractState (params) {
    return contractClient.query({ query: contracts.STATE, variables: params })
      .then(({ data: { contract_state: fields } }) => {
        return { fields: adjustEditability('contract', lodash.cloneDeep(fields)) }
      })
  }

  static async updateContractState (params) {
    return contractClient.mutate({ mutation: contracts.UPDATE_STATE, variables: params })
      .then(({ data: { update_contract_state: fields } }) => {
        return { fields: adjustEditability('contract', lodash.cloneDeep(fields)) }
      })
  }

  static async getContracts (params) {
    return contractClient.query({ query: contracts.LIST, variables: params })
  }

  static async getContractStatuses (params) {
    return contractClient.query({ query: contracts.STATUSES })
  }

  static async getReference (params) {
    // TODO: если в нескольких разных клиентах есть метод REFERENCE, то надо сюда прокидывать его имя
    return contractClient.query({ query: contracts.REFERENCE, variables: params })
  }

  static async getContractLogs (params) {
    return contractClient.query({ query: contracts.LOGS, variables: params })
  }

  static async getContractLogsStats (params) {
    return contractClient.query({ query: contracts.LOGS_STATS, variables: params })
  }

  static async getContractStats (params) {
    return contractClient.query({ query: contracts.STATS, variables: params })
  }

  static async getContractPositions (params) {
    // TODO: пока позиции не кешируем. Надо разобраться как удалить закешированные страницы пагинации,
    // при добавлении/удалении позиций....
    return contractClient.query({ query: contracts.POSITIONS, variables: params })
      .then(({ data: { contract_position: { positions, totalCount, actions } } }) => {
        return { positions, totalCount, actions: adjustActions('contract', actions) }
      })
  }

  static async importContractPositions (params) {
    return contractClient.mutate({ mutation: contracts.IMPORT_CONTRACT_POSITIONS, variables: params })
      .then(({ data: { import_contract_positions: { errors, positions } } }) => {
        return { errors, positions }
      })
  }

  static async appendTenderWinnerPositions (params) {
    return contractClient.mutate({ mutation: contracts.APPEND_WINNER_POSITIONS, variables: params })
  }

  static async removeContractPositions (params) {
    return contractClient.mutate({ mutation: contracts.REMOVE_POSITIONS, variables: params })
  }

  static async createContractPosition (params) {
    return contractClient.mutate({ mutation: contracts.CREATE_POSITION, variables: params })
  }

  static async updateContractPosition (params) {
    return contractClient.mutate({ mutation: contracts.UPDATE_POSITION, variables: params })
  }

  static async getDocumentTemplates (params) {
    return contractClient.query({ query: contracts.DOCUMENT_TEMPLATES, variables: params })
  }

  static async loadDocumentTemplate (params) {
    return contractClient.query({ query: contracts.LOAD_DOCUMENT_TEMPLATE, variables: params })
  }

  static async createDocumentTemplate (params) {
    return contractClient.mutate({ mutation: contracts.CREATE_DOCUMENT_TEMPLATE, variables: params })
  }

  static async updateDocumentTemplate (params) {
    return contractClient.mutate({ mutation: contracts.UPDATE_DOCUMENT_TEMPLATE, variables: params })
  }

  static async createDocument (params) {
    return contractClient.mutate({ mutation: contracts.CREATE_DOCUMENT, variables: params })
  }

  static async updateDocument (params) {
    return contractClient.mutate({ mutation: contracts.UPDATE_DOCUMENT, variables: params })
  }

  static async getDocuments (params) {
    return contractClient.query({ query: contracts.DOCUMENTS, variables: params })
  }

  static async renderDocument (params) {
    return contractClient.mutate({ mutation: contracts.RENDER_DOCUMENT, variables: params })
  }

  static async deleteDocuments (params) {
    return contractClient.mutate({ mutation: contracts.DELETE_DOCUMENTS, variables: params })
  }

  static async getCompanyInfo (params) {
    return contractClient.query({ query: contracts.COMPANY_INFO, variables: params })
  }

  static async getUserInfo (params) {
    return contractClient.query({ query: contracts.USER_INFO, variables: params })
  }

  static async createOrder (params) {
    return contractClient.mutate({ mutation: orders.CREATE, variables: params })
  }

  static async getOrders (params) {
    return contractClient.query({ query: orders.LIST, variables: params })
  }

  static async getOrderStatuses (params) {
    return contractClient.query({ query: orders.STATUSES })
  }

  static async getOrderStats (params) {
    return contractClient.query({ query: orders.STATS, variables: params })
  }

  static async getOrderState (params) {
    return contractClient.query({ query: orders.STATE, variables: params })
      .then(({ data: { order_state: fields } }) => {
        return { fields: adjustEditability('order', lodash.cloneDeep(fields)) }
      })
  }

  static async updateOrderState (params) {
    return contractClient.mutate({ mutation: orders.UPDATE_STATE, variables: params })
      .then(({ data: { update_order_state: fields } }) => {
        return { fields: adjustEditability('order', lodash.cloneDeep(fields)) }
      })
  }

  static async getOrderPositions (params) {
    // TODO: пока позиции не кешируем. Надо разобраться как удалить закешированные страницы пагинации,
    // при добавлении/удалении позиций....
    return contractClient.query({ query: orders.POSITIONS, variables: params })
      .then(({ data: { order_position: { positions, totalCount, actions } } }) => {
        return { positions, totalCount, actions: adjustActions('order', actions) }
      })
  }

  static async appendContractPositions (params) {
    return contractClient.mutate({ mutation: orders.APPEND_CONTRACT_POSITIONS, variables: params })
  }

  static async removeOrderPositions (params) {
    return contractClient.mutate({ mutation: orders.REMOVE_POSITIONS, variables: params })
  }

  static async deleteOrderPosition (params) {
    return contractClient.mutate({ mutation: orders.DELETE_POSITION, variables: params })
  }

  static async createOrderPosition (params) {
    return contractClient.mutate({ mutation: orders.CREATE_POSITION, variables: params })
  }

  static async updateOrderPosition (params) {
    return contractClient.mutate({ mutation: orders.UPDATE_POSITION, variables: params })
  }

  static async getOrderLogs (params) {
    return contractClient.query({ query: orders.LOGS, variables: params })
  }

  static async getOrderLogsStats (params) {
    return contractClient.query({ query: orders.LOGS_STATS, variables: params })
  }

  static async emailTemplate (params) {
    return contractClient.query({ query: email.TEMPLATE, variables: params })
      .then(({ data: { emailTemplate } }) => {
        return { emailTemplate }
      })
  }

  static async sendEmail (params) {
    return contractClient.mutate({ mutation: email.SEND_EMAIL, variables: params })
  }

  static async fileTemplate (params) {
    return contractClient.query({ query: file.TEMPLATE, variables: params })
      .then(({ data: { file_template: { data, name } } }) => {
        return { data, name }
      })
  }

  static async getUsers (usersIds) {
    // TODO: implement backend api
    return Promise.resolve({ data: { users: [] } })
  }

  static async getCrossEntitiesRelations (params) {
    // TODO: implement backend api
    return Promise.resolve({ data: { entityRelations: {} } })
  }

  static async generateReport (params) {
    return window.axios({
      url: process.env.VUE_APP_REPORT_GQL_ENDPOINT,
      params: params,
      method: 'GET',
      responseType: 'blob'
    })
  }
}
